'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useRef } from 'react';
import type { MutableRef } from '@/app/lcv/components/sections/spartan/shared/webgpu/canvas-basic/types';

// WHERE THE ACTIVE SLIDE'S CLIP HAS GOT TO, for the pagination ring.
//
// A ref rather than state on purpose. This is written every frame, and
// the page above these slides re-renders expensively -- three video
// sections and every prop feeding the WebGPU canvas. Nothing here may
// cause a render; the ring reads the same bag in its own frame loop and
// writes the SVG attribute directly.
//
// THE READING CARRIES THE SLIDE IT BELONGS TO. On a hand-over the
// outgoing slide's loop and the incoming one's overlap by a frame or
// two, and without the stamp the new bullet would briefly paint the old
// clip's position.
export interface SectionVideoProgress {
  index: number;
  progress: number;
}
export const createSectionVideoProgress = (): SectionVideoProgress => ({
  index: -1,
  progress: 0
});
interface SectionVideoClip {
  videoRef: RefObject<HTMLVideoElement | null>;
  endProgress?: number;
}
interface UseSectionVideoProgressOptions {
  index: number;
  // In playback order. Several clips read as ONE timeline, weighted by
  // their durations, because that is what the viewer is watching: the
  // story's connection clip and its main clip are one continuous picture.
  clips: SectionVideoClip[];
  progressRef: MutableRef<SectionVideoProgress>;
  enabled: boolean;
}
export const useSectionVideoProgress = t0 => {
  const $ = _c(7);
  const {
    index,
    clips,
    progressRef,
    enabled
  } = t0;
  const clipsRef = useRef(clips);
  const bagRef = useRef(progressRef);
  let t1;
  if ($[0] !== clips || $[1] !== progressRef) {
    t1 = () => {
      clipsRef.current = clips;
      bagRef.current = progressRef;
    };
    $[0] = clips;
    $[1] = progressRef;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(t1);
  const hasReportedRef = useRef(false);
  let t2;
  let t3;
  if ($[3] !== enabled || $[4] !== index) {
    t2 = () => {
      if (!enabled) {
        return;
      }
      const bag = bagRef.current;
      const resumed = hasReportedRef.current && bag.value.index === index ? bag.value.progress : 0;
      hasReportedRef.current = true;
      bag.value.index = index;
      bag.value.progress = resumed;
      if (resumed >= 1) {
        return;
      }
      let frame = 0;
      let reported = resumed;
      const read = () => {
        let elapsed = 0;
        let total = 0;
        for (const clip of clipsRef.current) {
          const element = clip.videoRef.current;
          const length = element && Number.isFinite(element.duration) ? element.duration * (clip.endProgress ?? 1) : 0;
          if (!element || length <= 0) {
            return reported;
          }
          total = total + length;
          elapsed = elapsed + Math.min(element.currentTime, length);
        }
        if (total <= 0) {
          return reported;
        }
        return Math.max(reported, Math.min(1, elapsed / total));
      };
      const tick = () => {
        reported = read();
        bag.value.index = index;
        bag.value.progress = reported;
        if (reported >= 1) {
          frame = 0;
          return;
        }
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
      return () => {
        if (frame) {
          cancelAnimationFrame(frame);
        }
      };
    };
    t3 = [enabled, index];
    $[3] = enabled;
    $[4] = index;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
};